import PropTypes from 'prop-types'
import ContentfulJsonLdBlocks from '../../src/components/shared/ContentfulJsonLdBlocks'
import React from 'react'
import PressListingService from '../../src/services/PressListingService'
import Layout from '../../src/components/layouts'
import NewsRoomPageLayout from '../../src/components/shared/NewsRoomLayout'
import NewsCategoryLayout from '../../src/components/shared/NewsCategoryLayout'
import { generateSeoData } from '../../src/common/nextSeoUtils'
import { getFullUrlFromReq } from '../../src/common/serverUtils'
import logger from '../../src/common/logger'
import WithSeoOverrides from 'src/components/shared/WithSeoOverride'
import { getTrustPilotRating } from 'src/common/getTrustPilotRating'
import { DEFAULT_RATING_DATA } from '../../src/common/constants/schemaConstants'
import { getMegamenuData } from 'src/common/back/megamenu'
import { getContentfulJsonLd } from 'src/common/getContentfulJsonLd'

export async function getServerSideProps(context) {
  const {
    params: { pid = [] },
    req,
    preview,
  } = context
  let megamenuData = null
  let data
  const [prefix, page] = pid
  let jsonLdSchemas = null

  let trustMarker = {
    ratingValue: DEFAULT_RATING_DATA.ratingValue,
    ratingCount: DEFAULT_RATING_DATA.ratingCount,
    reviewCount: DEFAULT_RATING_DATA.reviewCount,
  }

  if (
    (typeof prefix !== 'undefined' && prefix !== 'page') ||
    (typeof page !== 'undefined' && Number.isNaN(Number(page))) ||
    page < 1
  ) {
    return {
      notFound: true,
    }
  }

  const currentPage = Number(page) || 1

  if (currentPage === 1 && prefix) {
    return {
      redirect: {
        destination: '/press-releases/',
      },
    }
  }

  try {
    const [listing, jsonLd] = await Promise.all([
      PressListingService.getInstance().getListing('press-releases', {
        page: currentPage,
        preview,
      }),
      getContentfulJsonLd('press-releases/'),
    ])
    data = listing
    jsonLdSchemas = jsonLd
  } catch (error) {
    logger.error('PressReleases page: GraphQL listing failed', { error })

    return { notFound: true }
  }

  const pagePid = ['press-releases']
  const { canonicalUrl } = getFullUrlFromReq(req, pagePid)
  const seoData = generateSeoData({
    title: `Press Releases ${
      currentPage === 1 ? '' : `| Page ${currentPage} of ${data.countOfPages} `
    }| Freedom Debt Relief`,
    description:
      'An archive of all Freedom Debt Relief and Freedom Financial Network press releases from 2017 to present.',
    url: canonicalUrl,
  })

  if (currentPage === 1 && !data.items?.length) {
    return {
      notFound: true,
    }
  }

  try {
    trustMarker = await getTrustPilotRating(preview, trustMarker)
    megamenuData = await getMegamenuData(context)
  } catch (error) {
    logger.error('PressReleases page: trust marker fetch failed', { error })

    return {
      props: {
        data,
        seoData,
        currentPage,
        trustMarker,
        megamenuData: megamenuData || null,
        jsonLdSchemas,
      },
    }
  }

  return {
    props: {
      data,
      seoData,
      currentPage,
      trustMarker,
      megamenuData: megamenuData || null,
      jsonLdSchemas,
    },
  }
}

const PressReleases = ({
  data,
  seoData,
  currentPage,
  trustMarker = {},
  megamenuData,
  jsonLdSchemas,
}) => {
  return (
    <Layout
      footerClassName="max-w-1400"
      isBrandPage
      megamenuData={megamenuData}
    >
      <WithSeoOverrides {...seoData} />
      <ContentfulJsonLdBlocks jsonLdSchemas={jsonLdSchemas} />

      <NewsRoomPageLayout title="Press Releases">
        <NewsCategoryLayout data={data} title="Press Releases" />
      </NewsRoomPageLayout>
    </Layout>
  )
}

PressReleases.propTypes = {
  data: PropTypes.object.isRequired,
  seoData: PropTypes.object.isRequired,
  currentPage: PropTypes.number,
}

export default PressReleases
